'use client';
import { Switch } from '@headlessui/react';
import { cn } from '@ui/lib/utils';
import { ReactNode } from 'react';

export function ToggleCheckBox({
  isChecked,
  onChange,
  size = 'default',
  checkedIcon,
  uncheckedIcon,
  checkedColor,
  disabled = false,
  focusRingColor,
  className,
}: {
  isChecked: boolean;
  onChange: (checked: boolean) => void;
  size?: 'default' | 'sm' | 'xs';
  checkedIcon?: ReactNode;
  uncheckedIcon?: ReactNode;
  checkedColor?: string;
  disabled?: boolean;
  focusRingColor?: string;
  className?: string;
}) {
  // Size configurations with proper icon sizing
  const sizeClasses = {
    default: {
      container: 'h-6 w-11',
      circle: 'h-5 w-5',
      translate: 'translate-x-5',
      labelMargin: 'ml-3',
      iconSize: 'h-3 w-3', // Proper breathing room
    },
    sm: {
      container: 'h-5 w-9',
      circle: 'h-4 w-4',
      translate: 'translate-x-4',
      labelMargin: 'ml-2',
      iconSize: 'h-2.5 w-2.5', // Slightly larger for better visibility
    },
    xs: {
      container: 'h-4 w-7',
      circle: 'h-3 w-3',
      translate: 'translate-x-3',
      labelMargin: 'ml-1',
      iconSize: 'h-2 w-2', // Minimum viable size
    },
  };

  const currentSize = sizeClasses[size];

  // Helper function to render icons with consistent sizing
  const renderIcon = (icon: ReactNode) => {
    if (!icon) return null;

    // Ensure icon gets proper sizing and container
    return (
      <span
        className={cn(
          'flex items-center justify-center flex-shrink-0',
          currentSize.iconSize,
          // Add overflow protection
          'overflow-visible'
        )}
      >
        {icon}
      </span>
    );
  };

  return (
    <div className={cn('flex items-center', className)}>
      <Switch
        checked={isChecked}
        onChange={onChange}
        disabled={disabled}
        className={cn(
          isChecked
            ? checkedColor
              ? checkedColor
              : 'bg-green-700'
            : 'bg-slate-200',
          `relative inline-flex ${currentSize.container} flex-shrink-0 cursor-pointer disabled:opacity-80 disabled:cursor-not-allowed rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2`,
          focusRingColor ? focusRingColor : 'focus:ring-green-700',
          'focus:ring-offset-2'
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            isChecked ? currentSize.translate : 'translate-x-0',
            `pointer-events-none inline-flex items-center justify-center ${currentSize.circle} transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`,
            // Add padding to prevent icon clipping
            'p-0.5'
          )}
        >
          {/* Unified container behavior for both checked and unchecked icons */}
          {isChecked && checkedIcon && renderIcon(checkedIcon)}
          {!isChecked && uncheckedIcon && renderIcon(uncheckedIcon)}
        </span>
      </Switch>
    </div>
  );
}
